// "This capture died without finishing" — written by useRecorder when the document
// goes away mid-recording, read by useIndexDb's finalless rebuild.
//
// Why it exists: the rebuild gate's authority is TIME. A finalless set is only
// touched once its newest segment is older than STALE_REBUILD_MS (120s), because
// from the outside a young finalless set is indistinguishable from a recording still
// in progress — and rebuilding a live recording would upload half an encounter.
//
// That guess is unnecessary when the capture context itself knows it is being torn
// down. Closing the Chrome extension's side panel mid-recording destroys the
// document: the microphone is released, the rotation timer stops, and the set is
// left finalless with no final marker — measured in production on 2026-08-19, where
// 2m10s of audio sat unrecovered for seven minutes until an unrelated page load
// happened to run a drain pass. The panel can say so on the way out, and then the
// rebuild is a fact rather than a 120-second inference.
//
// localStorage on purpose: it is SYNCHRONOUS, so it survives a pagehide that
// destroys the document before any IndexedDB transaction could commit. Viewer-scoped
// and recency-capped like the heartbeat and recovery stores next to it.
//
// This marker never AUTHORISES a rebuild on its own — the heartbeat block and the
// active-capture check still apply, so a marker left by a crashed tab cannot cause
// the rebuild of a recording that another tab is still capturing.
import { recordingViewerKey } from "./recordingViewerKey";

const PREFIX = "recordingInterrupted:";
const MAX_IDS = 50;

function storeKey() {
  return PREFIX + recordingViewerKey();
}

function readMap() {
  try {
    const raw = localStorage.getItem(storeKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_IDS) {
      // Oldest first — a marker's whole life is one page load.
      keys.sort((a, b) => (map[a] || 0) - (map[b] || 0));
      for (let i = 0; i < keys.length - MAX_IDS; i++) delete map[keys[i]];
    }
    localStorage.setItem(storeKey(), JSON.stringify(map));
  } catch {
    // Best-effort: without the marker the 120s staleness path still recovers it.
  }
}

// Called from a pagehide handler, so it must stay synchronous and never throw.
export function markCaptureInterrupted(recordingId) {
  if (!recordingId) return;
  const map = readMap();
  map[recordingId] = Date.now();
  writeMap(map);
}

export function wasCaptureInterrupted(recordingId) {
  if (!recordingId) return false;
  return !!readMap()[recordingId];
}

export function clearCaptureInterrupted(recordingId) {
  if (!recordingId) return;
  const map = readMap();
  if (map[recordingId] != null) {
    delete map[recordingId];
    writeMap(map);
  }
}
